import { css, useTheme } from '@emotion/react';
import {
  Bold,
  Download,
  IndentDecrease,
  IndentIncrease,
  Italic,
  List,
  ListOrdered,
  NotepadTextDashed,
  SeparatorHorizontal,
  Underline,
} from 'lucide-react';

import { spacing } from '@/theme/tokens';

interface FormatState {
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isBulletList: boolean;
  isNumberedList: boolean;
}

interface EditorToolbarProps {
  formatState: FormatState;
  onBold: () => void;
  onItalic: () => void;
  onUnderline: () => void;
  onBulletList: () => void;
  onNumberedList: () => void;
  onIndent: () => void;
  onOutdent: () => void;
  onInsertSectionBreak: () => void;
  onInsertSpeakerNote: () => void;
  onExport?: () => void;
}

function ToolbarButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const theme = useTheme();

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active}
      onClick={onClick}
      css={css`
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 36px;
        min-height: 36px;
        border-radius: 4px;
        cursor: pointer;
        transition:
          background 0.15s,
          color 0.15s;
        color: ${active ? 'white' : theme.colors.textSecondary};
        background: ${active ? theme.colors.primary : 'transparent'};

        &:hover {
          background: ${active ? theme.colors.primary : theme.colors.surface};
          color: ${active ? 'white' : theme.colors.text};
        }
      `}
    >
      {children}
    </button>
  );
}

function Separator() {
  const theme = useTheme();
  return (
    <div
      aria-hidden="true"
      css={css`
        width: 1px;
        height: 20px;
        background: ${theme.colors.border};
        margin: 0 ${spacing.xs};
        flex-shrink: 0;
      `}
    />
  );
}

export function EditorToolbar({
  formatState,
  onBold,
  onItalic,
  onUnderline,
  onBulletList,
  onNumberedList,
  onIndent,
  onOutdent,
  onInsertSectionBreak,
  onInsertSpeakerNote,
  onExport,
}: EditorToolbarProps) {
  const theme = useTheme();

  return (
    <div
      role="toolbar"
      aria-label="Formatting options"
      css={css`
        display: flex;
        align-items: center;
        gap: 2px;
        padding: ${spacing.xs} ${spacing.sm};
        border-bottom: 1px solid ${theme.colors.border};
        flex-shrink: 0;
      `}
    >
      {/* Text formatting */}
      <ToolbarButton label="Bold" active={formatState.isBold} onClick={onBold}>
        <Bold size={16} aria-hidden="true" />
      </ToolbarButton>

      <ToolbarButton label="Italic" active={formatState.isItalic} onClick={onItalic}>
        <Italic size={16} aria-hidden="true" />
      </ToolbarButton>

      <ToolbarButton label="Underline" active={formatState.isUnderline} onClick={onUnderline}>
        <Underline size={16} aria-hidden="true" />
      </ToolbarButton>

      <Separator />

      {/* List formatting */}
      <ToolbarButton label="Bullet list" active={formatState.isBulletList} onClick={onBulletList}>
        <List size={16} aria-hidden="true" />
      </ToolbarButton>

      <ToolbarButton
        label="Numbered list"
        active={formatState.isNumberedList}
        onClick={onNumberedList}
      >
        <ListOrdered size={16} aria-hidden="true" />
      </ToolbarButton>

      <Separator />

      {/* Indentation */}
      <ToolbarButton label="Decrease indent" onClick={onOutdent}>
        <IndentDecrease size={16} aria-hidden="true" />
      </ToolbarButton>

      <ToolbarButton label="Increase indent" onClick={onIndent}>
        <IndentIncrease size={16} aria-hidden="true" />
      </ToolbarButton>

      <Separator />

      {/* Section break */}
      <ToolbarButton label="Insert section break" onClick={onInsertSectionBreak}>
        <SeparatorHorizontal size={16} aria-hidden="true" />
      </ToolbarButton>

      {/* Speaker note */}
      <ToolbarButton label="Insert speaker note" onClick={onInsertSpeakerNote}>
        <NotepadTextDashed size={16} aria-hidden="true" />
      </ToolbarButton>

      {onExport && (
        <>
          <div
            css={css`
              flex: 1;
            `}
          />
          <ToolbarButton label="Export" onClick={onExport}>
            <Download size={16} aria-hidden="true" />
          </ToolbarButton>
        </>
      )}
    </div>
  );
}
